import { useRef, useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import * as THREE from 'three';
import { Stack } from "@mui/material";

import FullScreenInit from "components/FullScreenInit";
import VirtualTourHeader from "components/VirtualTourHeader";

import { usePtcldScriptContext } from "contexts/PtcldScriptContext";

import PointCloudView from "./components/PointCloudView";
import { useGetOutputPointcloud } from "api/output/queries/useGetOutputPointcloud";

import { useAtom } from "jotai";
import { getPotreeCameraAtom } from "store/atoms/potree/getPotreeCamera";
import { isPotreeFocuesAtom } from "store/atoms/potree/isPotreeFocusedAtom";
import { setPoreeCameraAtom } from "store/atoms/virtualTourViewer/setPotreeCameraAtom";
import { useGetOutputQuery } from "api/output/queries/useGetOutputQuery";
import { getHTTPFileUrlFromS3Path, getHTTPUrlFromS3Path } from "utils/outputs";
import { Height } from "@mui/icons-material";
import { getMiniMapSize } from "utils/potree";
import { useGetScanTransformationQuery } from "api/scan/queries/useGetScanTransformationQuery";
import { useGetScanQuery } from "api/scan/queries/useGetScanQuery";
import { getScanOutputHTTPURL } from "utils/scan";
import { useLoading } from "contexts/LoadingContext";

type Props = {
  pointCloudData: PointCloudOutput | undefined;
  dataError: Error | null;
};
function PointCloudViewerContent({ pointCloudData, dataError }: Props) {
    const { 
      scriptsLoaded,
      scriptError,
      loadingProgression,
      setLoadingProgression,
      pointcloudLoading,
      setPointcloudLoading,
      ifcLoading,
      setIfcLoading,
      isSplit,
      setIsSplit,
      error,
      setError
    } = usePtcldScriptContext();

    const { scan1Data } = useLoading();
  
    const potreeContainerDiv = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<Potree.Viewer | null>(null);

    const [searchParams] = useSearchParams();
    const scanIdFromQueryParams = searchParams.get("scan") || "";
    const firstScanId = searchParams.get("scan1") || "";
    const { outputId = "", scanId: scanIdFromParams = ""} = useParams();

    const[getPotreeCamera,setgetPotreeCamera] = useAtom(getPotreeCameraAtom);
    const [isPotreeFocused, setIsPotreeFocused]=useAtom(isPotreeFocuesAtom); 
    const [isPotreeCameraSetString,setPotreeCameraString]=useAtom(setPoreeCameraAtom);
    const [currentView,setCurrentView]=useState<any>(null);

    const scanId = scanIdFromParams || scanIdFromQueryParams || firstScanId;

    const { data: scanTransformation } = useGetScanTransformationQuery(scanId);
    
      //const scanId = "866e5d90-7fc7-449d-a2a0-de36c422c830";
      const {
        data: scan,
        isLoading: scanLoading,
        error: scanError,
      } = useGetScanQuery(scanId);

    const { data: output } = useGetOutputQuery(scan?.outputs?.filter(
      (output) => output.type === "VIRTUALTOUR")[0]?.id || outputId);

    const floorImageUrl = useMemo(() => {
      return scan1Data?.referenceFloorPlanAlignedImageURL;
    }, [scan1Data]);
    // const {
    //   data: projectScanTransformation,
    //   isLoading: projectScanTransformationLoading,
    //   error: projectScanTransformationError,
    // } = useGetScanTransformationRelativeToProjectQuery(scanId);
  
    // console.log(projectScanTransformation);
    let currentFocuedImage:null|any;

    function calculateDistance(
      x: number, 
      x1: number, 
      y: number, 
      y1: number, 
      z: number, 
      z1: number
  ): number {
  //   console.log("We are calling calculate distance "+Math.sqrt(
  //     Math.pow(x1 - x, 2) +
  //     Math.pow(y1 - y, 2) +
  //     Math.pow(z1 - z, 2)
  // ));
      return Math.sqrt(
          Math.pow(x1 - x, 2) +
          Math.pow(y1 - y, 2) +
          Math.pow(z1 - z, 2)
      );
  }
   useEffect(()=>{
    return()=>{
      setIsPotreeFocused(null);
      setCurrentView(null);
      setgetPotreeCamera(null);
    }
   },[])
    useEffect(()=>{
      //
      //console.log("this is the getPotreeCamera",getPotreeCamera ,isPotreeCameraSet);
      if(isPotreeCameraSetString && currentView && isPotreeFocused && getPotreeCamera){
        //console.dir(getPotreeCamera);
        //console.dir(currentView?.scene?.view);
        //console.log("This is the potree ",isPotreeCameraSet);
        //console.log("this is the position of the camera "+getPotreeCamera.position.x+ " "+getPotreeCamera.position.y+" "+getPotreeCamera.position.z);
        // console.log(
        //   "This is the rotation of the camera: " +
        //     getPotreeCamera.rotation.x* (180 / Math.PI)+ " " +
        //     getPotreeCamera.rotation.y * (180 / Math.PI)+ " " +
        //     getPotreeCamera.rotation.z* (180 / Math.PI) +" "+isPotreeCameraSet.rotation.y
        // );
        let isPotreeCameraSet=JSON.parse(isPotreeCameraSetString);
        
        const m = isPotreeCameraSet.transformationMatrix; // your object
        const matrixArray = [
          m.e00, m.e10, m.e20, m.e30,
          m.e01, m.e11, m.e21, m.e31,
          m.e02, m.e12, m.e22, m.e32,
          m.e03, m.e13, m.e23, m.e33
        ];
  
  
        const transformationMatrix = new THREE.Matrix4().fromArray(matrixArray);
        //console.log("this is the transformation matrix ",transformationMatrix); 
        const conversion = new THREE.Matrix4();
        conversion.set(
          1, 0, 0, 0,
          0, 0, -1, 0,
          0, 1, 0, 0,
          0, 0, 0, 1
        );
  
        // Compute the inverse of the conversion matrix
        const conversionInv = conversion.clone().invert();
  
        // Compose: M' = C * M * C⁻¹
        const result = conversion.clone()
          .multiply(transformationMatrix)
          .multiply(conversionInv);
  
        // Use the transformed position for the camera
        const decomposedPosition = new THREE.Vector3();
        const decomposedQuaternion = new THREE.Quaternion();
        const decomposedScale = new THREE.Vector3();
  
        result.decompose(decomposedPosition, decomposedQuaternion, decomposedScale);
        //console.log(decomposedPosition, decomposedQuaternion, decomposedScale);
        const euler = new THREE.Euler();
        euler.setFromQuaternion(decomposedQuaternion, 'YZX'); // or your desired order

        const degX = THREE.MathUtils.radToDeg(euler.x);
        const degY = THREE.MathUtils.radToDeg(euler.y);
        const degZ = THREE.MathUtils.radToDeg(euler.z);
  
        // Apply your transformation
        const newDegX = 90 - degX;
        const newDegY = degY;
        const newDegZ = 180 - degZ;
  
        // Convert back to radians
        const newEuler = new THREE.Euler(
          THREE.MathUtils.degToRad(newDegX),
          THREE.MathUtils.degToRad(newDegY),
          THREE.MathUtils.degToRad(newDegZ),
          'YZX' // Same order as before
        );
  
        // Convert back to quaternion
        const newQuaternion = new THREE.Quaternion().setFromEuler(newEuler);
        // Set the camera position
        if (isPotreeCameraSet.isFocused == false)
        currentView?.changeCamera(
          -decomposedPosition.x,
          decomposedPosition.y,
          decomposedPosition.z,
          0.05 // or your desired value
        );
  
        // Set the camera rotation
        currentView?.changeRotation(
          newQuaternion.x,
          newQuaternion.y,
          newQuaternion.z,
          newQuaternion.w,
          0.05, // or your desired value
          isPotreeCameraSet.isFocused // or your desired value
        );
        currentView?.changeFov(isPotreeCameraSet.fov);
        if (isPotreeCameraSet.isFocused == true) {
          let closestImage: any | null;
          let minDistance = Infinity;
          isPotreeFocused?.images.forEach((image: any) => {
            const distance = calculateDistance(
              -decomposedPosition.x,
              image.position[0],
              decomposedPosition.y,
              image.position[1],
              decomposedPosition.z,
              image.position[2]
            );
  
          if (distance < minDistance) {
              minDistance = distance;
              closestImage = image;
          }
      });
        if(closestImage && isPotreeFocused?.focusedImage==null || isPotreeFocused?.focusedImage!==closestImage){
        isPotreeFocused?.unfocus();
        //console.dir(currentView?.view);
        
          isPotreeFocused?.focus(closestImage);
          
        }
      }
      else{
        if(isPotreeFocused?.focusedImage!=null)
        isPotreeFocused?.unfocus();
      }

      }
    },[isPotreeCameraSetString,currentView,isPotreeFocused,getPotreeCamera])
    const calledOnce=useRef(false);
    useEffect(()=>{

      if(isPotreeCameraSetString && currentView && calledOnce.current==false){
        let isPotreeCameraSet=JSON.parse(isPotreeCameraSetString);
        //console.log("We are setting the camera");
        // const outputUrl = getHTTPUrlFromS3Path(`s3://bucketname/${scanId}/virtualtour/vtour/thumbnail.png`);
        const outputUrl = getScanOutputHTTPURL(scanId, 'virtualtour') + 'thumbnail.png';
        //console.log("this is the output path ",output)
        //const outputUrl="http://localhost:8001/thumbnail.png";
        currentView.setUnityView(true);
        if (
          floorImageUrl &&
          scan1Data?.scanTransformation?.referenceFloor?.metadata
        ) {
          currentView.setFloorPlan(
            floorImageUrl,
            {
              x: scan1Data.scanTransformation?.referenceFloor
                ?.metadata?.obj?.centerX,
              y: scan1Data.scanTransformation?.referenceFloor
                ?.metadata?.obj?.centerY,
            },
            scan1Data.scanTransformation?.referenceFloor?.metadata
              ?.obj?.lengthX,
            scan1Data.scanTransformation?.referenceFloor?.metadata
              ?.obj?.lengthY
          );
          currentView.miniMapPosition(
            0, 
            0,
            getMiniMapSize(),
            floorImageUrl,
            {
              x: scan1Data.scanTransformation?.referenceFloor
                ?.metadata?.obj?.centerX,
              y: scan1Data.scanTransformation?.referenceFloor
                ?.metadata?.obj?.centerY,
            },
            scan1Data.scanTransformation?.referenceFloor?.metadata
              ?.obj?.lengthX,
            scan1Data.scanTransformation?.referenceFloor?.metadata
              ?.obj?.lengthY
          );
          currentView.floorPlanEnabled = true;
          currentView.thumbnailEnabled = false;
        }
        currentView.setThumbnail(outputUrl,{x:isPotreeCameraSet.thumbnail.center[0],y:isPotreeCameraSet.thumbnail.center[1]},isPotreeCameraSet.thumbnail.width,isPotreeCameraSet.thumbnail.height);
        if (!currentView.floorPlanEnabled) {
          currentView.miniMapPosition(0,0,getMiniMapSize(),outputUrl,{x:isPotreeCameraSet.thumbnail.center[0],y:isPotreeCameraSet.thumbnail.center[1]},isPotreeCameraSet.thumbnail.width,isPotreeCameraSet.thumbnail.height);
          currentView.floorPlanEnabled = false;
          currentView.thumbnailEnabled = true;
        }
        calledOnce.current=true;
      }
    },[isPotreeCameraSetString,currentView]);
    
    

    useEffect(() => {
      //setgetPotreeCamera(null);
      const viewerElem = potreeContainerDiv.current;
      setPointcloudLoading(true);
      if (scriptsLoaded && pointCloudData) {
        // setPointcloudLoading(true); // eralier doing this but creating fluctation
        const Potree = window.Potree;
        const proj4 = window.proj4;
        if (!Potree) {
          setError("Potree library not found.");
          console.error("Potree library not found.");
          setPointcloudLoading(false);
          return;
        }
        if (viewerElem) {
          viewerRef.current = new Potree.Viewer(viewerElem);
          viewerRef.current.controls.enabled=false;
          const camera = viewerRef.current?.scene.cameraP;
          //console.dir(camera);
          setgetPotreeCamera(camera);
          //console.dir(getPotreeCamera);
          let matrix: number[][] = [];
          if (scanTransformation) {
            matrix = scanTransformation.scanWrtWorld;
            console.log("matrix",matrix);
            // matrix = [[2, 0, 0, 0], [0, 2, 0, 0], [0, 0, 2, 0], [0, 0, 0, 1]];
          }
         const Image360 = async (
          coordinatesUrl: string,
          imageUrl: string
        ) => {
          try {
            if (viewerRef.current) {
              proj4.defs(
                "WGS84",
                "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs"
              );
              proj4.defs("pointcloud", viewerRef.current.getProjection());
              const transform = proj4("WGS84", "pointcloud");
 
              const params = {
                transform: transform,
              };
 
              // Load 360 images
              Potree.Images360Loader.load(
                coordinatesUrl,
                viewerRef.current,
                imageUrl,
                params,
                matrix
              ).then((images) => {
                console.log("Loaded 360 images", images);
                if (viewerRef.current) {
                  viewerRef.current.scene.add360Images(images);
                  setIsPotreeFocused(viewerRef.current?.scene?.images360[0]);
                }
              });
            }
          } catch (err) {
            console.error("Error in run function:", err);
          }
        };
  
          const OrientedImage = async (
            coordinatesUrl: string,
            imageUrls: { [key: string]: string | null },
            xmlfile: string
          ) => {
            try {
              // Load oriented images
              if (viewerRef.current) {
                Potree.OrientedImageLoader.load(
                  xmlfile,
                  coordinatesUrl,
                  viewerRef.current,
                  imageUrls
                ).then((images) => {
                  if (viewerRef.current) {
                    viewerRef.current.scene.addOrientedImages(images);
                  }
                });
              }
            } catch (err) {
              console.error("Error in run function:", err);
            }
          };
  
          try {
            if (viewerRef.current) {
              viewerRef.current.setEDLEnabled(true);
              viewerRef.current.setFOV(60);
              viewerRef.current.setPointBudget(1 * 1000 * 1000);
              viewerRef.current.setClipTask(Potree.ClipTask.HIGHLIGHT);
              viewerRef.current.loadSettingsFromURL();
              viewerRef.current.setBackground("black");
  
              viewerRef.current.setControls(viewerRef.current.orbitControls);
  
              viewerRef.current.loadGUI(() => {
                if (viewerRef.current) {
                  viewerRef.current.setLanguage("en");
                  const menuAppearance =
                    document.getElementById("menu_appearance");
                  if (menuAppearance && menuAppearance.nextElementSibling) {
                    const sibling =
                      menuAppearance.nextElementSibling as HTMLElement;
                    sibling.style.display = "block";
                  } else {
                    console.error(
                      "Next sibling of #menu_appearance not found."
                    );
                  }
                }
              });
  
              const url = getHTTPFileUrlFromS3Path(pointCloudData.metadata);
            const hierarchyUrl = getHTTPFileUrlFromS3Path(pointCloudData.hierarchy);
            const octreeUrl = getHTTPFileUrlFromS3Path(pointCloudData.octree);
            const otherPath = {
              octree: octreeUrl,
              hierarchy: hierarchyUrl,
            };
  
            Potree.loadPointCloud(url, otherPath, matrix).then(
              (e) => {
                if (viewerRef.current) {
                  const pointcloud = e.pointcloud;
                  console.log("pointcloud split", pointcloud);
                  pointcloud.name = "ptcld";
                  const material = pointcloud.material;
 
                  material.activeAttributeName = "rgba";
                  material.minSize = 2;
                  material.size = 0.5;
                  material.pointSizeType = Potree.PointSizeType.ADAPTIVE;
 
                  viewerRef.current.scene.addPointCloud(pointcloud);
                  viewerRef.current.fitToScreen();
                  viewerRef.current.setPointBudget(5_000_000);
                  setCurrentView(viewerRef.current);
                  // viewerRef.current.useHQ = true;
 
                  // 360 images check
                  if (
                    pointCloudData.coordinates360 &&
                    pointCloudData.vtImage360Path
                  ) {
                    const coordinates360path = getHTTPFileUrlFromS3Path(pointCloudData.coordinates360);
                    const images360path = getHTTPUrlFromS3Path(pointCloudData.vtImage360Path);
                    Image360(
                      coordinates360path,
                      images360path
                    );
                    viewerRef.current.setClassifications(Potree.ClassificationScheme.PREIMAGE);
                  }
  
                    // Oriented/simple images check
                    if (
                      pointCloudData.coordinatesOriented &&
                      pointCloudData.xmlfile &&
                      pointCloudData.imageOriented
                    ) {
                      OrientedImage(
                        pointCloudData.coordinatesOriented,
                        pointCloudData.imageOriented,
                        pointCloudData.xmlfile
                      );
                    }
  
                    // viewerRef.current.mapView.showSources(false);
                    setLoadingProgression(100);
                  }
                },
                (e) => {
                  console.error("ERROR loading point cloud:", e);
                  setPointcloudLoading(false);
                }
              );

              if (pointCloudData.ifc) {
                setIfcLoading(true);
                const ifc = new Potree.IFC(viewerRef.current);
                ifc
                  .load(
                    pointCloudData.ifc,
                    viewerRef.current.scene,
                    "ifc",
                    true,
                    matrix
                  )
                  .then((mesh) => {
                    // mesh: unknown
                    {
                      setIfcLoading(false);
                    }
                  });
                ifc
                  .load(
                    pointCloudData.ifc,
                    viewerRef.current.scene2,
                    "split-ifc",
                    false,
                    matrix
                  )
                  .then((mesh: unknown) => {
                    console.log("IFC loaded", mesh);
                  });
              }
  
              // Potree.loadPointCloud(url, otherPath).then(
              //   (e) => {
              //     if (viewerRef.current) {
              //       const pointcloud = e.pointcloud;
              //       pointcloud.name = "split-ptcld";
              //       pointcloud.visible = false;
              //       const material = pointcloud.material;
  
              //       material.activeAttributeName = "rgba";
              //       material.minSize = 2;
              //       material.pointSizeType = Potree.PointSizeType.FIXED;
  
              //       viewerRef.current.scene2.addPointCloud(pointcloud);
              //       viewerRef.current.fitToScreen();
              //       viewerRef.current.mapView.showSources(false);
              //       setPointcloudLoading(false);
              //     }
              //   },
              //   (e) => {
              //     console.error("ERROR loading point cloud:", e);
              //     setPointcloudLoading(false);
              //   }
              // );
            }
          } catch (err) {
            setError("Failed to initialize Potree viewer.");
            console.error("Error loading point cloud:", err);
            setPointcloudLoading(false);
          }
        }
      }
      return () => {
        document.querySelectorAll(".sp-container, .ui-slider, .i18n-container").forEach((element) => {
          element.remove();
        });
  
        if (viewerRef.current) {
          console.log("Viewer ref exists, clearing it", viewerRef.current);
          setCurrentView(null);
          viewerRef.current.renderer.clear();
          //viewerRef.current.dispose();
          viewerRef.current = null;
          const profileWindow = document.getElementById("profile_window");
          if (profileWindow && profileWindow.parentNode) {
            profileWindow.parentNode.removeChild(profileWindow);
          }
        }
      };
    }, [pointCloudData, scriptsLoaded]);
  
    if (error || scriptError || dataError) {
      return (
        <FullScreenInit
          displayText={error || scriptError || dataError?.message}
          animation="error"
          hideBackButton
          height="100%"
        />
      );
    }
  
    return (
      <Stack height="0%" direction="column">
        {/* {!fullScreen && (
          <VirtualTourHeader
            headingName={"Point Cloud"}
            name1={"Project"}
            name2={"scan"}
            type={"pointcloud"}
          />
        )} */}
        <PointCloudView potreeContainerDiv={potreeContainerDiv} viewerRef={viewerRef} />
      </Stack>
    );
  }

export default PointCloudViewerContent;